/**
 * ACIL Intelligence - Reset Password Page
 */
import { useState, useMemo } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import api, { formatApiError } from '../utils/api';
import { Shield, Eye, EyeOff, AlertCircle, CheckCircle2, ArrowLeft } from 'lucide-react';
import '../App.css';

function getStrength(pwd) {
  let score = 0;
  if (pwd.length >= 8) score++;
  if (/[A-Z]/.test(pwd)) score++;
  if (/[0-9]/.test(pwd)) score++;
  if (/[^A-Za-z0-9]/.test(pwd)) score++;
  return score;
}

const strengthLabels = ['Muy debil', 'Debil', 'Regular', 'Buena', 'Fuerte'];
const strengthColors = ['#EF4444', '#F59E0B', '#F59E0B', '#22C55E', '#22C55E'];

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const strength = useMemo(() => getStrength(password), [password]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError('Las contrasenas no coinciden');
      return;
    }
    if (strength < 4) {
      setError('La contrasena debe tener minimo 8 caracteres, una mayuscula, un numero y un caracter especial');
      return;
    }
    setLoading(true);
    try {
      await api.post('/api/auth/reset-password', { token, password });
      setSuccess(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="auth-page">
        <div className="auth-card" style={{ textAlign: 'center' }}>
          <AlertCircle size={40} color="var(--danger)" style={{ marginBottom: 16 }} />
          <h1 className="auth-title" style={{ fontSize: 22 }}>Token no proporcionado</h1>
          <p className="auth-subtitle">Este enlace no es valido.</p>
          <Link to="/" className="btn-primary" style={{ textDecoration: 'none', display: 'inline-flex' }}>
            Ir al login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-page" data-testid="reset-password-page">
      <div className="auth-card">
        <div className="auth-logo">
          <div className="auth-logo-icon"><Shield size={22} /></div>
          <span className="auth-logo-text">ACIL Intelligence</span>
        </div>

        {success ? (
          <div style={{ textAlign: 'center' }} data-testid="reset-success">
            <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'rgba(34,197,94,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <CheckCircle2 size={28} color="var(--success)" />
            </div>
            <h1 className="auth-title" style={{ fontSize: 22 }}>Contrasena actualizada</h1>
            <p className="auth-subtitle">Ya puedes iniciar sesion con tu nueva contrasena.</p>
            <Link to="/" className="btn-primary" style={{ textDecoration: 'none', display: 'inline-flex' }} data-testid="go-to-login-btn">
              Ir al login
            </Link>
          </div>
        ) : (
          <>
            <h1 className="auth-title" style={{ fontSize: 22 }}>Nueva contrasena</h1>
            <p className="auth-subtitle">Crea una contrasena segura para tu cuenta.</p>

            {error && (
              <div className="auth-error-banner" data-testid="reset-error">
                <AlertCircle size={16} /><span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} data-testid="reset-password-form">
              <div style={{ marginBottom: 16, position: 'relative' }}>
                <label className="acil-label">Nueva contrasena</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPwd ? 'text' : 'password'}
                    className="acil-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    style={{ paddingRight: 48 }}
                    data-testid="new-password-input"
                  />
                  <button type="button" className="password-toggle" onClick={() => setShowPwd(!showPwd)} tabIndex={-1}>
                    {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {password && (
                  <div data-testid="password-strength">
                    <div className="strength-bar">
                      <div className="strength-fill" style={{ width: `${strength * 25}%`, background: strengthColors[strength] }} />
                    </div>
                    <div style={{ fontSize: 11, color: strengthColors[strength], fontWeight: 600, marginTop: 4 }}>
                      {strengthLabels[strength]}
                    </div>
                  </div>
                )}
              </div>

              <div style={{ marginBottom: 24 }}>
                <label className="acil-label">Confirmar contrasena</label>
                <input
                  type="password"
                  className="acil-input"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  data-testid="confirm-password-input"
                />
              </div>

              <button type="submit" className="btn-primary" disabled={loading} style={{ width: '100%', height: 48 }} data-testid="reset-submit-btn">
                {loading ? <><div className="acil-spinner" /> Actualizando...</> : 'Actualizar contrasena'}
              </button>
            </form>

            <div style={{ textAlign: 'center', marginTop: 20 }}>
              <Link to="/" style={{ fontSize: 13, color: 'var(--muted)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <ArrowLeft size={14} /> Volver al login
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
